import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PropTypes from "prop-types";
import CalendarIcon from "@rsuite/icons/Calendar";
import TimeIcon from "@rsuite/icons/Time";
import TagIcon from "@rsuite/icons/Tag";
import MemberIcon from "@rsuite/icons/Member";
import EmailIcon from "@rsuite/icons/Email";
import { dateFormatter } from "../../utils/dateUtils";
import {
  UAL_ACTIONS,
  UAL_ACTION_DETAIL_LABELS,
  UAL_ACTION_LABELS,
} from "../../constants/ualActions";
import hotelIcon from "../../assets/img/hotel.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

// Leading icon for each applied-filter summary chip (matches the Figma design).
export const ChipIcon = ({ chipKey }) => {
  if (chipKey === "hotelIds") {
    return (
      <img src={hotelIcon} alt="" className="user-activity-summary__icon-img" />
    );
  }
  if (chipKey === "hotelCode") {
    return <TagIcon className="user-activity-summary__icon-svg" />;
  }
  if (chipKey === "userName") {
    return <MemberIcon className="user-activity-summary__icon-svg" />;
  }
  if (chipKey === "userEmail") {
    return <EmailIcon className="user-activity-summary__icon-svg" />;
  }
  if (chipKey === "actionDate") {
    return <CalendarIcon className="user-activity-summary__icon-svg" />;
  }
  if (chipKey === "actionTime") {
    return <TimeIcon className="user-activity-summary__icon-svg" />;
  }
  return null;
};

ChipIcon.propTypes = { chipKey: PropTypes.string };

// Timezone used for the "Time (EST)" column.
const EST_TZ = "America/New_York";

export const DEFAULT_PAGE_SIZE = 10;

// The drill-down asks for every detail doc in one page, not DEFAULT_PAGE_SIZE.
// An ARI edit fans out to one ARI_COMBO_EDITED doc per rate plan x room type x date —
// a stop-sell over 2 rate plans, 2 room types and 10 days is 24 — and the modal renders
// one aggregated Applied To (distinct rate plans, room types, and the full date range)
// with no paging control of its own. At 10 the aggregate silently described the first
// page only: a date range that stopped short and rate plans that never appeared.
// 200 is the API's MAX_PAGE_SIZE (UalFields.MAX_PAGE_SIZE).
export const DETAILS_PAGE_SIZE = 200;

// Form mirrors the User Activity filter design: Hotel Name (property_id), Hotel
// Code (property_code), User Name + User Email (type-ahead → actor_id), Action
// Date (from/to) and Action Time (time_from/tz — a time-of-day floor on each day).
export const INIT_FORM_VALUE = {
  hotelIds: [],
  // The full property objects behind hotelIds. The picker searches server-side, so a
  // chosen hotel may not be in the 20-item property store — we keep its object here so
  // the summary chip and the re-opened Edit-search picker can still show its name.
  hotelProperties: [],
  hotelCode: "",
  userName: null, // selected user { id, name, email } from the name picker
  userEmail: null, // selected user { id, name, email } from the email picker
  actionDate: null, // [startDate, endDate] from DateRangePicker
  actionTime: null, // Date (time-of-day) from DatePicker
};

// Canonical stored actions (from the UAL registry) — used to label the Action
// column. Sourced from the shared UAL_ACTIONS taxonomy so the read screen and
// the write call sites that emit the `source` header stay in sync.
export const ACTION_OPTIONS = Object.values(UAL_ACTIONS).map((value) => ({
  label: UAL_ACTION_LABELS[value],
  value,
}));

export const actionLabel = (action) => UAL_ACTION_LABELS[action] || action;

// The shared label carries the area ("ARI — Stop Sell"); beside the area pill the
// prefix is dropped.
const AREA_PREFIX = /^[^\u2014]*\u2014\s*/;

// The action text shown beside the area pill ("Stop Sell", "Repushed VCC", …), in
// both the results grid and the Activity Details Type row. Null when the action
// has no label.
export const actionDescription = (action) => {
  const detailLabel = UAL_ACTION_DETAIL_LABELS[action];
  if (detailLabel) return detailLabel;
  const label = UAL_ACTION_LABELS[action];
  return label ? label.replace(AREA_PREFIX, "") : null;
};

// Landing notice (EO-1835): logging went live per area on different dates.
export const UAL_LOGS_AVAILABLE_FROM = {
  ariResReport: "Sep 10, 2026",
  promo: "Sep 18, 2026",
};
export const LOGS_AVAILABLE_NOTICE = `Logs are available for user actions from ${UAL_LOGS_AVAILABLE_FROM.ariResReport} (ARI, Res Report) and ${UAL_LOGS_AVAILABLE_FROM.promo} (Promo) onwards. Actions taken before these dates are not available here.`;

export const GRID_HELPER_TEXT = "Click any row to view full details";

// Search must be bounded by a hotel: Hotel Name OR Hotel Code is required (either one
// satisfies it). User (name/email) and date are optional filters — not mandatory.
export const MANDATORY_SEARCH_GROUPS = [
  {
    key: "hotel",
    message: "Please select at least 1 hotel to view logs.",
    isSet: (f) => Boolean(f.hotelIds?.length) || Boolean(f.hotelCode?.trim()),
  },
];

// Returns a human-readable error listing the missing groups, or null when the
// filters satisfy every mandatory group.
// The mandatory groups the form is currently missing, keyed by group key —
// e.g. { hotel: true }. Drives the red borders on the offending fields, the same
// way the Price Check form keys its `validationErrors` by field.
export const getMissingSearchGroups = (formValue) => {
  const f = formValue || {};
  return MANDATORY_SEARCH_GROUPS.reduce(
    (acc, g) => (g.isSet(f) ? acc : { ...acc, [g.key]: true }),
    {}
  );
};

export const getSearchValidationError = (formValue) => {
  const f = formValue || {};
  const missing = MANDATORY_SEARCH_GROUPS.find((g) => !g.isSet(f));
  return missing ? missing.message : null;
};

// Area → badge styling (class names defined in userActivity.scss). Keys match
// the `area` field returned by GET /ual/events.
export const AREA_BADGES = {
  ARI: { label: "ARI", className: "badge-ari" },
  // The registry emits area "Promotions" (ual-registry/promotions.yml); the pill reads
  // "Promo" per Figma. Keyed on the emitted value — keying it on "Promo" meant every promo
  // row missed the map and fell through to the neutral badge-default with the raw
  // "Promotions" text (EO: promo Type pill doesn't match Figma). "Promo" stays as an alias
  // the same way Reservation aliases ReservationReport below.
  Promotions: { label: "Promo", className: "badge-promo" },
  Promo: { label: "Promo", className: "badge-promo" },
  UserManagement: { label: "User Management", className: "badge-user-mgmt" },
  ReservationReport: { label: "Res Report", className: "badge-res-report" },
  Reservation: { label: "Res Report", className: "badge-res-report" },
};

// Options for the results "action type" multi-select filter (matches the `area`
// field). No "all" entry — an empty selection already means every type.
export const ACTION_TYPE_OPTIONS = [
  { label: "ARI", value: "ARI" },
  // value is sent verbatim as the `area` query param and matched exactly against
  // area.keyword, so it has to be the emitted "Promotions", not the display label.
  { label: "Promo", value: "Promotions" },
  // User Management is left out until its logging is integrated (EO-1835).
  { label: "Res Report", value: "ReservationReport" },
];

// Results table columns — mirrors the Figma layout. `sortable` columns drive the
// server-side sort (sort_by/sort_order); their dataKeys are the API sort_by values.
export const COLUMNS = [
  {
    dataKey: "hotel_name",
    label: "Hotel Name",
    minWidth: 170,
    flexGrow: 1.2,
    sortable: true,
  },
  { dataKey: "hotel_code", label: "Hotel Code", width: 120 },
  { dataKey: "actor_name", label: "User Name", minWidth: 150, flexGrow: 1 },
  {
    dataKey: "actor_email",
    label: "User Email",
    minWidth: 200,
    flexGrow: 1.2,
  },
  { dataKey: "date", label: "Date", width: 130, sortable: true },
  { dataKey: "time_local", label: "Time (local)", width: 120 },
  { dataKey: "time_est", label: "Time (EST)", width: 120 },
  // Sorts on the backing `action` keyword (ARI_BULK_EDITED, RESERVATION_VCC_REPUSHED…),
  // which groups the rows by what was done — the cell itself shows the free-text
  // summary, so the order reads by action rather than alphabetically by sentence.
  {
    dataKey: "type",
    label: "Action",
    minWidth: 240,
    flexGrow: 1.6,
    sortable: true,
  },
];

const pad = (n) => String(n).padStart(2, "0");
const formatChipTime = (t) => `${pad(t.getHours())}:${pad(t.getMinutes())}`;

// IANA zone the wall-clock Action Time was picked in, e.g. "Asia/Kolkata". The backend
// needs it to resolve that time to instants (and to get DST right across a range).
const browserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );

// Collect the actor_id values from the two user pickers (deduped).
const actorIds = (filters) =>
  [filters?.userName?.id, filters?.userEmail?.id]
    .filter((id) => id !== undefined && id !== null)
    .map(String)
    .filter((id, i, arr) => arr.indexOf(id) === i);

/**
 * The filter half of the query — everything that narrows the result set, shared by the
 * table search and the Excel export (the export differs only in that it takes no paging
 * params: it walks the whole result set server-side).
 * Multi-value filters are repeated params (property_id=1&property_id=2).
 */
const appendFilterParams = (
  params,
  filters,
  { areas, q, sortBy, sortOrder }
) => {
  const { hotelIds, hotelCode, actionDate, actionTime } = filters || {};

  if (actionDate?.length === 2) {
    params.append("from", startOfDay(actionDate[0]).toISOString());
    params.append("to", endOfDay(actionDate[1]).toISOString());
  }
  // Action Time is a time-of-day lower bound applied to EVERY day in the range, so it
  // travels as its own param rather than being folded into `from` (folding it in only
  // ever clipped the first day, which made the filter look ignored on a multi-day range).
  // `tz` tells the backend which clock the wall-clock time was picked against — the same
  // one the "Time (local)" column renders in.
  if (actionTime) {
    params.append("time_from", formatChipTime(actionTime));
    params.append("tz", browserTimeZone());
  }
  (hotelIds || []).forEach((id) => params.append("property_id", id));
  if (hotelCode?.trim()) {
    params.append("property_code", hotelCode.trim());
  }
  actorIds(filters).forEach((id) => params.append("actor_id", id));
  // Action-type filter — applied server-side across the whole result set, not just
  // the loaded page. Repeated params (area=ARI&area=Promo); empty means every type.
  (areas || []).forEach((area) => params.append("area", area));
  // Free-text search box. Matched server-side against summary_text, property_names
  // and property_codes (see UalFields.QUERY_FIELDS).
  if (q?.trim()) {
    params.append("q", q.trim());
  }
  // Server-side column sort (Hotel Name / Date). The table dataKeys map 1:1 to
  // the backend `sort_by` values; sortOrder is the rsuite asc/desc token.
  if (sortBy && sortOrder) {
    params.append("sort_by", sortBy);
    params.append("sort_order", sortOrder);
  }
};

/**
 * Build the GET /ual/events query string from the applied filters plus pagination.
 */
export const buildSearchQuery = (filters, pagination = {}) => {
  const params = new URLSearchParams();
  const { cursor, direction, size = DEFAULT_PAGE_SIZE } = pagination;

  appendFilterParams(params, filters, pagination);

  params.append("size", String(size));
  if (cursor) {
    params.append("cursor", cursor);
    params.append("direction", direction || "next");
  }
  return `?${params.toString()}`;
};

/**
 * Build the GET /ual/events/export query string: the same filters the table is showing,
 * minus paging — the backend walks every matching event, not the page on screen. `tz` is
 * always sent (not just with the Action Time filter) because the export's "Time (local)"
 * column has to be rendered in the same clock the table displays.
 */
export const buildExportQuery = (filters, options = {}) => {
  const params = new URLSearchParams();
  appendFilterParams(params, filters, options);
  if (!params.has("tz")) {
    params.append("tz", browserTimeZone());
  }
  return `?${params.toString()}`;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const formatChipDate = (d) =>
  `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

// Applied-filter chips shown above the results table.
export const buildSearchSummary = (filters, properties = []) => {
  const summary = [];
  const {
    hotelIds,
    hotelProperties,
    hotelCode,
    userName,
    userEmail,
    actionDate,
    actionTime,
  } = filters || {};

  if (hotelIds?.length) {
    // Resolve each id's name from the objects the picker captured, falling back to the
    // property store for anything seeded from there. Either source can be short (a hotel
    // beyond the store's first page), so we key by id rather than trusting list order.
    const byId = new Map(
      [...(properties || []), ...(hotelProperties || [])].map((p) => [
        p.propertyId,
        p.propertyName,
      ])
    );
    // find, not filter: only the first resolved name is displayed — the rest of the array was
    // built and thrown away. extraCount counts hotelIds, not names, so nothing else needs it.
    const firstName = hotelIds.map((id) => byId.get(id)).find(Boolean);
    summary.push({
      key: "hotelIds",
      label: "Property",
      value: firstName,
      extraCount: hotelIds.length > 1 ? hotelIds.length - 1 : 0,
    });
  }
  if (hotelCode) {
    summary.push({ key: "hotelCode", label: "Hotel Code", value: hotelCode });
  }
  if (userName?.name) {
    summary.push({ key: "userName", label: "User Name", value: userName.name });
  }
  if (userEmail?.email) {
    summary.push({
      key: "userEmail",
      label: "User Email",
      value: userEmail.email,
    });
  }
  if (actionDate?.length === 2) {
    summary.push({
      key: "actionDate",
      label: "Action Date",
      value: `${formatChipDate(actionDate[0])} - ${formatChipDate(actionDate[1])}`,
    });
  }
  if (actionTime) {
    summary.push({
      key: "actionTime",
      label: "Action Time",
      value: formatChipTime(actionTime),
    });
  }
  return summary;
};

// The registry types ARI availability as a plain boolean, but the product words it
// Open/Closed — that is the language of the ARI calendar the edit was made on, where a
// date is open or closed for sale. Map the field key to the format the
// modal renders it with, overriding the registry hint. Other booleans (Single Use,
// Open to Arrival…) keep Yes/No. `price` is typed `currency` in the registry but the
// stored value is a per-LoS/occupancy structure, so it needs its own renderer.
//
// Overrides for values the registry cannot type correctly on its own. ARI's availability
// is a plain boolean in the registry but the product words it Open/Closed; `price` is
// typed `currency` there while the stored value is a per-LoS/occupancy structure.
//
// Nothing else belongs here. This map is keyed by FIELD NAME across every area, so an
// entry claims that name everywhere: a `status` override added for the promo
// enable/disable header also captured the reservation report's VCC status field and
// rendered it "Repushed" instead of "Re-pushed". Where the registry can say it, the
// registry says it — those promo cells declare `format: enum_label` themselves.
const FIELD_FORMAT_OVERRIDES = { isOpen: "availability", price: "price" };

export const resolveFieldFormat = (fieldKey, format) =>
  FIELD_FORMAT_OVERRIDES[fieldKey] || format;

/**
 * A USD amount as the design writes it: "$155", "$120.50".
 *
 * Module-private on purpose. `src/constants/utils.js` already exports a `formatCurrency`
 * that nine components use; a second export under the same name would be a trap for the
 * next reader. That one always prints two decimals ("$1,234.00") and takes a currency
 * argument, which is right for its callers and wrong for this screen, where the design
 * writes a whole rate as "$155".
 *
 * `currencyDisplay: "narrowSymbol"` is what keeps the "$". The default, "symbol",
 * disambiguates a foreign currency against the reader's own — so a browser on en-GB, where
 * "$" already means something else, rendered every rate as "US$155". Only the reader's
 * locale decided it, which is why it looked right in an en-US browser and in tests. The
 * shared helper dodges the same bug by forcing the en-US locale; pinning just the symbol
 * leaves the reader their own grouping and separators.
 */
const formatCurrency = (n) => {
  const amount = Number(n);
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    // Cents only when there are cents: a whole rate reads "$155" as the design writes it,
    // and a fractional one keeps both digits — a flat 0 minimum rendered 120.50 as
    // "$120.5", which is not how money is written.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

// ARI price is stored as [{ los, adult: [rate per occupancy…], child: [...] }]. Render
// each entry's adult rates as a currency list, prefixed by "LoS n:" only when more than
// one length-of-stay is present. Falls back to a plain string for anything unexpected.
const formatPrice = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    if (value === null || value === undefined || value === "") return "—";
    // A surface that edits one flat nightly rate sends the number itself rather than the
    // per-LoS/occupancy structure. It is still money, and printing a bare "100" next to a
    // "$155" from the structured path made the same field look like two different units.
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : formatCurrency(n);
  }
  const multiLos = value.length > 1;
  return value
    .map((entry) => {
      const rates = Array.isArray(entry?.adult)
        ? entry.adult.map(formatCurrency).join(", ")
        : "";
      if (!rates) return "";
      return multiLos && entry?.los != null
        ? `LoS ${entry.los}: ${rates}`
        : rates;
    })
    .filter(Boolean)
    .join("; ");
};

// UAL sends booleans as either a real boolean or the string "true".
const isTruthyFlag = (v) => v === true || v === "true";

// Unknown/list formats render the same way: join an array, stringify anything else.
const joinOrString = (v) => (Array.isArray(v) ? v.join(", ") : String(v));

// Compound source resolves to [from, to]; render as "from – to", or a single date if only one
// end is set. An aggregate's non-contiguous dates arrive as a list of such pairs, and each
// pair renders the same way.
const formatDateRange = (value) => {
  if (Array.isArray(value) && value.some(Array.isArray)) {
    return value.map((range) => formatDateRange(range)).join(", ");
  }
  const parts = (Array.isArray(value) ? value : [value])
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map((v) => dateFormatter(v));
  if (parts.length === 0) return "—";
  return parts.length > 1 ? `${parts[0]} – ${parts.at(-1)}` : parts[0];
};

/**
 * A SCREAMING_SNAKE enum key as sentence case: "TOTAL_PRICE_BEFORE_TAX" -> "Total price
 * before tax", "CUSTOM" -> "Custom". Deliberately derived from the key rather than looked
 * up in a dictionary of known values: the promo vocabularies live in the producer
 * (PromotionActions in ual-context-promotions) and calculationType is not a closed set
 * here at all, so a dictionary would silently fall back to the raw key the day one of
 * them gains a member. Anything that is not an enum key — a numeric code, a value the
 * producer already wrote in prose — is left exactly as it is.
 */
const humanizeEnum = (value) => {
  const text = String(value);
  if (!/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(text)) return text;
  const words = text.toLowerCase().split("_");
  return (
    words[0].charAt(0).toUpperCase() +
    words[0].slice(1) +
    (words.length > 1 ? " " + words.slice(1).join(" ") : "")
  );
};

/**
 * "Calculation Method" — which price the adjustment is computed against. The snapshot
 * stores the producer's key; the sentence is UI copy, so it is worded here rather than
 * upstream. That keeps the wording changeable without a producer release, and means
 * events already indexed against the key pick up the phrasing too.
 *
 * An unknown key falls through to {@link humanizeEnum} rather than to a placeholder: a
 * vocabulary this map has not caught up with should still read as a value, not as a gap.
 */
const CALCULATION_METHODS = new Map([
  ["TOTAL", "Apply discount to total price (before tax)"],
]);

const formatCalculationMethod = (value) =>
  CALCULATION_METHODS.get(String(value)) || humanizeEnum(value);

/**
 * Promo Change Log renderers. Each takes the values the registry's `sources` composed, in
 * declared order, and words the row the way the design does — the read side deliberately
 * hands over the raw parts so the phrasing stays here, changeable without a producer
 * release and applying to events already indexed.
 */

// A single count with its unit: "3 days", "1 day", "0 days". ARI stores each restriction
// as a bare number, and a bare "3" in a Change Log row does not say three of what.
const formatCount = (unit, units) => (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n} ${n === 1 ? unit : units}`;
};

// "min 3 nights" / "2 to 7 nights" / "max 7 nights". Null bounds are absent conditions.
// A RANGE, unlike formatCount above — the promo rows compose a min and a max into one row.
const formatBounds = (unit, units) => (value) => {
  const [min, max] = Array.isArray(value) ? value : [value, null];
  const has = (v) => v !== null && v !== undefined && v !== "";
  const word = (n) => (Number(n) === 1 ? unit : units);
  if (has(min) && has(max)) return `${min} to ${max} ${word(max)}`;
  if (has(min)) return `min ${min} ${word(min)}`;
  if (has(max)) return `max ${max} ${word(max)}`;
  return "—";
};

// The producer renders blackout windows as one string: "2026-09-15 to 2026-09-16
// (stay-through), 2026-09-18 to 2026-09-19 (stay-through)". Split it back into windows and
// date-format each, so they read like every other date on the screen. The stay-through
// flag is its own row now, so the suffix is dropped here.
const BLACKOUT_WINDOW = /^\s*(\S+)\s+to\s+(\S+?)(?:\s*\(stay-through\))?\s*$/;

const formatBlackoutWindows = (value) => {
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  const windows = text
    .split(/,\s*(?=\d{4}-)/)
    .map((part) => {
      const m = BLACKOUT_WINDOW.exec(part);
      // An unparseable window is shown as the producer wrote it rather than dropped:
      // losing a blackout date silently is worse than showing an unformatted one.
      return m ? formatDateRange([m[1], m[2]]) : part.trim();
    })
    .filter(Boolean);
  return windows.length > 0 ? windows.join(", ") : "—";
};

// Whether any blackout window is a stay-through exclusion. Read off the same string, which
// is the only place the producer records it.
const formatStayThrough = (value) => {
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return text.includes("(stay-through)") ? "Yes" : "No";
};

// "-10%" / "+400 flat" / "Multiple" — one signed figure, from
// [discountType, priceAdjustment, discountAmount, dayWiseDiscount].
const formatAdjustment = (value) => {
  const [discountType, priceAdjustment, amount, dayWise] = Array.isArray(value)
    ? value
    : [];
  // A day-wise offer has no single figure; the per-day detail is the next row.
  if (isTruthyFlag(dayWise)) return "Multiple";
  if (
    amount === null ||
    amount === undefined ||
    !discountType ||
    !priceAdjustment
  ) {
    // Never guess a direction or a type — that would assert a price change we were not told.
    return "—";
  }
  const figure =
    Number(amount) === Math.trunc(Number(amount))
      ? String(Math.trunc(Number(amount)))
      : String(amount);
  const sign = priceAdjustment === "DECREASE" ? "-" : "+";
  return discountType === "PERCENTAGE"
    ? `${sign}${figure}%`
    : `${sign}${figure} flat`;
};

// "All days", or the shape of a day-wise offer until the producer sends the breakdown.
const formatDaysAppliedOn = (value) => {
  const flag = Array.isArray(value) ? value[0] : value;
  return isTruthyFlag(flag) ? "Varies by day of week" : "All days";
};

/**
 * The VCC row on a reservation-report repush, as the design words it: "Re-pushed".
 *
 * The captured field is the repush's own status, which reads "Success" — it records
 * whether the call worked, not what was done. The row names the operation instead, which
 * is what the design writes and what a reader is looking for: a click event's Change Log
 * says what happened, and "Success" says nothing about the VCC.
 *
 * A status this map does not know falls through to sentence case, so a failed repush
 * still reads "Failed" rather than claiming one that succeeded. Keys are matched
 * upper-cased, since the producer writes "Success" where the other enums are SCREAMING.
 *
 * Worded here rather than upstream for the same reason as the calculation method — UI
 * copy, changeable without a producer release, and it applies to events already indexed.
 */
const VCC_STATUSES = new Map([
  ["SUCCESS", "Re-pushed"],
  ["REPUSHED", "Re-pushed"],
  ["ISSUED", "Issued"],
]);

const formatVccStatus = (value) =>
  VCC_STATUSES.get(String(value).toUpperCase()) || humanizeEnum(value);

/**
 * The UAL format hints, one renderer each. A lookup rather than a switch: the switch scored 16 on
 * cognitive complexity because every case carried its own ternaries, even though the cases are
 * independent and none of them branch on each other.
 * <p>
 * A Map, not an object literal. `format` comes off the drill-down API response, so it is external
 * input: an object literal resolves inherited members, and a hint of "constructor" or "toString"
 * would return a prototype function, get called, and hand React a boxed object to render. A Map
 * has no prototype chain, so anything unrecognised misses cleanly. The switch this replaced could
 * not be reached that way, so the map introduced the exposure.
 */
const VALUE_FORMATTERS = new Map([
  ["datetime", (v) => dateFormatter(v, true)],
  ["date", (v) => dateFormatter(v)],
  ["date_range", formatDateRange],
  ["boolean", (v) => (isTruthyFlag(v) ? "Yes" : "No")],
  ["availability", (v) => (isTruthyFlag(v) ? "Open" : "Closed")],
  ["number", (v) => Number(v).toLocaleString()],
  [
    "currency",
    (v) =>
      Number(v).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        // Same reason as formatCurrency above: without this, en-GB reads "US$155".
        currencyDisplay: "narrowSymbol",
      }),
  ],
  ["price", (v) => formatPrice(v)],
  ["percentage", (v) => `${(Number(v) * 100).toFixed(0)}%`],
  ["string_list", joinOrString],
  ["enum_label", humanizeEnum],
  ["calculation", formatCalculationMethod],
  ["vcc_status", formatVccStatus],
  // Ranges (promo: one row composed from a min and a max) vs a single count (ARI: one
  // restriction per row). Named apart so a scalar can never land on the range renderer,
  // which would read a lone 3 as "min 3 days".
  ["nights_range", formatBounds("night", "nights")],
  ["days_range", formatBounds("day", "days")],
  ["days", formatCount("day", "days")],
  ["nights", formatCount("night", "nights")],
  ["blackout_windows", formatBlackoutWindows],
  ["stay_through", formatStayThrough],
  ["adjustment", formatAdjustment],
  ["days_applied", formatDaysAppliedOn],
]);

/**
 * Formats whose formatter owns the WHOLE value, so it must never be collapsed behind a
 * "+N" chip on its way there. Two kinds land here:
 *
 * - composed values, whose array is the ORDERED parts the read API assembled:
 *   `["FLAT", "INCREASE", 800, false]` is one adjustment, not four values, and
 *   `[3, null]` is one length-of-stay bound;
 * - values a formatter parses itself, like the producer's blackout string, which it
 *   splits into windows and date-formats. Left out, an array-shaped one collapsed into a
 *   chip list and the formatter never ran — the row then showed raw producer text with
 *   unformatted dates, and "Allow stay through" showed that text instead of Yes/No.
 *
 * `date_range` belongs here too but is handled separately by the caller, which also has
 * to recognise an aggregate's list OF ranges.
 */
const WHOLE_VALUE_FORMATS = new Set([
  "nights_range",
  "days_range",
  "adjustment",
  "blackout_windows",
  "stay_through",
]);

export const isWholeValueFormat = (format) => WHOLE_VALUE_FORMATS.has(format);

/**
 * Formats whose row states an OUTCOME rather than a transition, so it renders the value it
 * landed on and never a before → after pair.
 *
 * A VCC repush is the case: the snapshot carries the status on both sides, so the row
 * arrived as "Issued → Re-pushed". But the event is a click — the thing that happened is
 * the repush, and the design writes it as one word. Showing the prior state invites the
 * reader to treat a button press as a field edit, and puts From/To columns on a section
 * that has nothing to compare.
 */
const OUTCOME_FORMATS = new Set(["vcc_status"]);

export const isOutcomeFormat = (format) => OUTCOME_FORMATS.has(format);

/**
 * Render a raw value against a UAL format hint (closed vocabulary). Used by both
 * header cells and diff rows in the details modal. An unrecognised hint falls back to the same
 * rendering string_list uses, as the old default case did.
 */
export const formatValue = (value, format) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const formatter = VALUE_FORMATTERS.get(format);
  return formatter ? formatter(value) : joinOrString(value);
};

// Join a list-or-scalar value into a comma-separated string.
const joinList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value || value === 0 ? String(value) : "";
};

// Join a property array (name preferred, code fallback) for the results table.
// Accepts both the API's plural keys (property_names/property_codes) and the
// legacy singular keys.
export const displayProperty = (row) => {
  const names =
    row.property_names ||
    row.property_name ||
    row.property_codes ||
    row.property_code ||
    [];
  return Array.isArray(names) ? names.join(", ") : String(names);
};

// Hotel Name / Hotel Code accessors for the results table.
export const displayHotelName = (row) =>
  joinList(row.property_names || row.property_name) || "—";

export const displayHotelCode = (row) =>
  joinList(row.property_codes || row.property_code) || "—";

// The hotel's IANA zone, stamped onto every event by the read API (property_timezone).
// Absent only when the property has no zone configured.
export const hotelTimeZone = (row) => row?.property_timezone || null;

/**
 * The event's instant in a given zone. "Local" on this screen means the HOTEL's clock —
 * the one a revenue manager reasons in — not the reader's browser clock, so an edit made
 * at 09:00 in New York reads as 09:00 whether you open the page from Mumbai or Miami.
 * Falls back to the browser zone when the property carries no zone, and when the zone is
 * one dayjs can't resolve (rather than rendering nothing at all).
 */
const inZone = (iso, timeZone) => {
  const d = dayjs(iso);
  if (!timeZone) return d;
  try {
    return d.tz(timeZone);
  } catch {
    return d;
  }
};

// occurred_at → Date / Time (local) / Time (EST) for the split timestamp columns.
// Date follows the hotel's zone too: an edit at 23:30 in Honolulu belongs to the hotel's
// day, not to tomorrow because the reader happens to sit in Mumbai.
export const formatActivityDate = (iso, timeZone) =>
  iso && dayjs(iso).isValid()
    ? inZone(iso, timeZone).format("MMM D, YYYY")
    : "—";

export const formatActivityTimeLocal = (iso, timeZone) =>
  iso && dayjs(iso).isValid() ? inZone(iso, timeZone).format("HH:mm:ss") : "—";

export const formatActivityTimeEst = (iso) =>
  iso && dayjs(iso).isValid() ? dayjs(iso).tz(EST_TZ).format("HH:mm:ss") : "—";

// Header cells already shown in the fixed top grid (buildDetailHeader), keyed by the
// drill-down API's header keys — skipped when rendering the header as detail rows so
// they are not duplicated.
const TOP_GRID_HEADER_KEYS = new Set(["who", "when", "property", "email"]);

// Header cells the registry emits that "Applied To" does not show.
//   outcome  — every promo layout carries it, but it is always the SUCCESS of the call
//              itself: a failed mutation writes no event, so the row can only ever read
//              "SUCCESS" and tells an auditor nothing.
//   promo_id — the storage id of the derived rate plan behind the promo (PROMO_DELETED
//              only). The promo name identifies it on every screen a user works from.
const SUPPRESSED_HEADER_KEYS = new Set(["outcome", "promo_id"]);

// Registry labels the modal overrides. The registry names the cell after the context
// variable it reads (context.promo_name -> "Promo"); the block reads as a list of
// attributes, so it says which attribute of the promo this is.
const HEADER_LABEL_OVERRIDES = { promo: "Promo Name" };

// "Room Type" -> "Room Types". Left alone when it already reads as a plural, so a label
// the registry pluralised itself is not doubled up.
const pluralLabel = (label) =>
  !label || label.endsWith("s") ? label : `${label}s`;

// Distinct edit dates collapse into contiguous spans: Sep 1-3 is one range, while Sep 2 and
// Sep 11 with nothing between them stay two rather than being smeared into "Sep 2 – Sep 11".
// Each span is [start] or [start, end], the shape the date_range format renders.
const nextDay = (date) => dayjs(date).add(1, "day").format("YYYY-MM-DD");

// Sorted with an explicit comparator: the default sort compares UTF-16 code units, which
// happens to be right for ISO YYYY-MM-DD and silently wrong the day a value is not one.
// Saying so in code costs nothing and stops the guarantee resting on the format alone.
const byIsoDate = (a, b) => String(a).localeCompare(String(b));

const toDateRanges = (values) =>
  [...values].sort(byIsoDate).reduce((ranges, date) => {
    const current = ranges.at(-1);
    const previous = current?.at(-1);
    // An unparseable value yields "Invalid Date", which matches nothing, so it opens its
    // own range instead of silently joining the one before it.
    if (previous && nextDay(previous) === dayjs(date).format("YYYY-MM-DD")) {
      current[1] = date;
    } else {
      ranges.push([date]);
    }
    return ranges;
  }, []);

/**
 * "Applied To" for a whole activity rather than per detail doc.
 *
 * An ARI edit fans out to one ARI_COMBO_EDITED doc per date, so rendering each item's header
 * on its own repeated "Rate Plan / Room Type / Date" once per date. The design shows a single
 * block: the distinct rate plans and room types (collapsed behind "show all" by ListValue)
 * and one Date Range spanning the edit.
 *
 * Distinct values are collected in first-seen order. `date` collapses to the earliest and
 * latest as a date_range; every other key keeps its registry label and format.
 */
export const buildAggregatedHeaderRows = (items) => {
  const byKey = new Map();
  (items || []).forEach((item) => {
    buildHeaderDetailRows(item?.header).forEach((row) => {
      const entry = byKey.get(row.key) || {
        key: row.key,
        label: row.label,
        format: row.format,
        values: [],
      };
      const values = Array.isArray(row.value) ? row.value : [row.value];
      values.forEach((v) => {
        if (
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !entry.values.includes(v)
        ) {
          entry.values.push(v);
        }
      });
      byKey.set(row.key, entry);
    });
  });

  return [...byKey.values()]
    .filter((e) => e.values.length > 0)
    .map((e) => {
      if (e.key === "date") {
        // Dates read as ranges rather than the registry's per-doc "Date". The label follows
        // the count like every other key: one span is a "Date Range", several are "Date Ranges".
        const ranges = toDateRanges(e.values);
        return {
          key: e.key,
          label: ranges.length > 1 ? "Date Ranges" : "Date Range",
          format: "date_range",
          value: ranges.length > 1 ? ranges : ranges[0],
        };
      }
      const many = e.values.length > 1;
      return {
        key: e.key,
        // The label follows the value: one rate plan is a "Rate Plan", several are
        // "Rate Plans". The registry label is singular because it describes a single
        // detail doc; the aggregate can hold many.
        label: many ? pluralLabel(e.label) : e.label,
        format: e.format,
        value: many ? e.values : e.values[0],
      };
    });
};

/**
 * The Change Log for a whole activity: every distinct field change across the detail docs,
 * in first-seen order. A bulk edit that applied the same change to each date collapses to one
 * row; an upload that set a different price per date keeps one row per distinct pair.
 */
export const aggregateChangeFields = (items) => {
  const seen = new Set();
  const fields = [];
  (items || []).forEach((item) => {
    if (item?.body?.type !== "diff") return;
    (item.body.fields || []).forEach((f) => {
      const key = JSON.stringify([f.field, f.label, f.before, f.after]);
      if (!seen.has(key)) {
        seen.add(key);
        fields.push(f);
      }
    });
  });

  // A combo with no prior snapshot publishes `after` only, so the same change arrives twice:
  // once as before -> after and once as null -> after. They are one change, and showing the
  // half-empty row beside the full one reads as two different edits. Keep the row that has a
  // before; drop the blank one only when another row already records the same landing value.
  const hasBefore = (v) => v !== null && v !== undefined && v !== "";
  const documented = new Set(
    fields
      .filter((f) => hasBefore(f.before))
      .map((f) => JSON.stringify([f.field, f.after]))
  );
  return fields.filter(
    (f) =>
      hasBefore(f.before) || !documented.has(JSON.stringify([f.field, f.after]))
  );
};

// A header cell carries a value worth showing (non-empty scalar or non-empty list).
const headerCellHasValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * The drill-down API returns a per-item `header` map ({ key: { label, value, format } }).
 * Its who/when/property/email cells are already in the fixed top grid; the rest are the
 * per-action context the details table must show (ARI: Rate Plan / Room Type / Date;
 * Promo: Promo Name / Offer / …). Returns those remaining cells in map order, dropping
 * empty ones, the cells in SUPPRESSED_HEADER_KEYS, and applying HEADER_LABEL_OVERRIDES,
 * so the modal renders them as label/value rows above the body's changes.
 */
export const buildHeaderDetailRows = (header) => {
  if (!header || typeof header !== "object") return [];
  return Object.entries(header)
    .filter(
      ([key]) =>
        !TOP_GRID_HEADER_KEYS.has(key) && !SUPPRESSED_HEADER_KEYS.has(key)
    )
    .map(([key, cell]) => ({
      key,
      label: HEADER_LABEL_OVERRIDES[key] || cell?.label || key,
      value: cell?.value,
      format: cell?.format,
    }))
    .filter((row) => headerCellHasValue(row.value));
};

// Fixed top-of-modal info grid for the details view — common to every activity and
// read from the clicked row (summary); the sections below it vary per action and come
// from the drill-down API.
//
// Returned as ROWS of cells rather than a flat field list, because the design pairs
// them: Property spans the row, then User/Email and the two timestamps sit side by side.
// Each cell is {label, value}; a row of one cell stretches to full width.
//
// The design also merges what the results table splits — hotel name and code into one
// "Property" cell, and date + time into one "Date & Time" cell per zone — so the grid
// reads as four rows instead of seven fields.
export const buildDetailInfoRows = (row) => {
  const r = row || {};
  const tz = hotelTimeZone(r);
  const date = formatActivityDate(r.occurred_at, tz);
  const timeLocal = formatActivityTimeLocal(r.occurred_at, tz);
  const timeEst = formatActivityTimeEst(r.occurred_at);
  const property = [displayHotelName(r), displayHotelCode(r)]
    .filter((v) => v && v !== "\u2014")
    .join(" | ");

  return [
    [{ label: "Property", value: property || "\u2014" }],
    [
      { label: "User", value: r.actor_name || "\u2014" },
      { label: "Email", value: r.actor_email || "\u2014" },
    ],
    [
      {
        label: "Date & Time (local)",
        value: joinDateTime(date, timeLocal, " - "),
      },
      { label: "Date & Time (EST)", value: joinDateTime(date, timeEst, " ") },
    ],
  ];
};

// "Jul 31, 2026" + "13:00:23" -> "Jul 31, 2026 - 13:00:23". Kept apart when either
// half is missing, so a row with no timestamp shows the em dash rather than "— - —".
const joinDateTime = (date, time, sep) => {
  const dash = "\u2014";
  if (date === dash && time === dash) return dash;
  if (date === dash) return time;
  if (time === dash) return date;
  return `${date}${sep}${time}`;
};

// Both results-toolbar controls (action-type dropdown, search box) are applied
// server-side via `area` and `q` — see buildSearchQuery. The table renders exactly
// the rows the API returned, so there is no client-side row filter.
